import { AdminSelect, Button, Div, Modal, P } from '@vgrubs/components';
import { MenuItemV2Response } from 'components/src/types/menu.types';
import { useEffect, useState } from 'react';
import {
  RefineItemField,
  RefineMenuAiItemDto,
} from '../../../types/api/menuAi.types';
import {
  AspectRatioPicker,
  AspectRatioValue,
  DEFAULT_ASPECT_RATIO,
  toAspectRatioDto,
} from '../components/AspectRatioPicker';
import { PromptTextarea } from '../components/PromptTextarea';
import { SampleImagesInput } from '../components/SampleImagesInput';

/**
 * Per-item refine control. Picks a field (price/description/category/image) and
 * either a free-text prompt or (for image) a reference image URL, then calls
 * the synchronous refine-item endpoint via `onRefine`.
 */
export const RefineItemModal = ({
  item,
  locationId,
  brandId,
  loading,
  closeModal,
  onRefine,
}: {
  item: MenuItemV2Response | null;
  locationId: string | number;
  brandId: string | number;
  loading: boolean;
  closeModal: () => void;
  onRefine: (dto: RefineMenuAiItemDto) => Promise<void> | void;
}) => {
  const [field, setField] = useState<RefineItemField>('category');
  const [prompt, setPrompt] = useState('');
  const [exampleUrls, setExampleUrls] = useState<string[]>([]);
  const [ratio, setRatio] = useState<AspectRatioValue>(DEFAULT_ASPECT_RATIO);

  useEffect(() => {
    setField('category');
    setPrompt('');
    setExampleUrls([]);
    setRatio(DEFAULT_ASPECT_RATIO);
  }, [item]);

  const handleConfirm = async () => {
    if (!item) return;
    const dto: RefineMenuAiItemDto = { itemId: item.id, field };
    if (field === 'image') {
      if (exampleUrls[0]) dto.example = exampleUrls[0];
      // Ratio fields ride along for the image field only.
      Object.assign(dto, toAspectRatioDto(ratio));
    } else if (prompt.trim()) {
      dto.prompt = prompt.trim();
    }
    await onRefine(dto);
    closeModal();
  };

  const fieldOptions = (
    <optgroup label="Field to refine">
      <option value="category">Category</option>
      <option value="price">Price</option>
      <option value="name">Name</option>
      <option value="description">Description</option>
      <option value="prepNotes">Preparation Notes</option>
      <option value="image">Image</option>
    </optgroup>
  );

  return (
    <Modal
      isOpen={!!item}
      closeModal={closeModal}
      header={'Refine item with AI'}
      subtitle={item ? item.name : undefined}
      logo={false}
      zIndex={120}
      textAlign={'left'}
      footer={
        <Button $selected $w="100%" $loading={loading} onClick={handleConfirm}>
          Refine
        </Button>
      }
      $numOfFloatingButtons={1}
    >
      {item && (
        <Div $flex $column $gap="20px">
          <AdminSelect
            value={field}
            options={fieldOptions}
            label="What to refine"
            onChange={({ target }: { target: HTMLSelectElement }) =>
              setField(target.value as RefineItemField)
            }
          />

          {field === 'image' ? (
            <>
              <AspectRatioPicker
                value={ratio}
                onChange={setRatio}
                disabled={loading}
              />
              <div>
                <P $mb="8">Reference image (optional)</P>
                <Div $flex>
                  <SampleImagesInput
                    locationId={locationId}
                    brandId={brandId}
                    urls={exampleUrls}
                    onChange={setExampleUrls}
                    max={1}
                  />
                </Div>
              </div>
            </>
          ) : (
            <PromptTextarea
              label="Prompt"
              placeholder="e.g. highlight that it is gluten-free"
              value={prompt}
              onChange={({ target }) => setPrompt(target.value)}
              $textareaHeight="140"
              expandTitle="Edit prompt"
            />
          )}
        </Div>
      )}
    </Modal>
  );
};
